import { useState, type ReactNode } from "react";
import { getJson, postJson } from "../api";
import { RadioCards, Window, type MenuItem } from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, TextField, useToast } from "../ui/kit";
import { OnceSecret, PasswordField } from "../ui/pieces";
import { type PersonStatus } from "./organizations";
import styles from "./orgs.module.css";

// Acciones sobre una persona de la empresa (§6): Restablecer contraseña (`reset`), Cerrar sesiones (`revocar`),
// Desactivar (`desactivar-u`), Bloquear (`bloquear`), Desbloquear (toast con «Deshacer») y Abrir como usuario (`abrir`).
// Las usan la lista de Usuarios (⋮ de cada fila) y la ficha del usuario (⋮ del encabezado): una sola pieza, no copias.

export type Person = {
  userId: number;
  name: string;
  status: PersonStatus;
  isOwner: boolean;
  /** Sesiones abiertas si se conocen (la ficha); en la lista, el texto no dice cuántas. */
  sessions?: number | null;
};

type Reply = { saved?: boolean; field?: string | null; messageKey?: string; temporaryPassword?: string | null };

type Dialog = { kind: "reset" | "revoke" | "deactivate" | "block" | "open"; person: Person } | null;

const errorOf = (L: (key: string) => string, body: Reply | null | undefined) =>
  L(body?.messageKey ?? "ADMIN_ERR_GENERIC");

/**
 * Menú ⋮ y ventanas de las acciones sobre una persona. `onChanged` vuelve a leer la pantalla después de cada cambio.
 * `withBlock`: Bloquear… está en la ficha; en la lista, Desbloquear solo para quien está bloqueado.
 */
export function usePersonActions(orgId: number, orgName: string, onChanged: () => void) {
  const { L } = useTexts();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const base = `/api/admin/organizations/${orgId}/members`;

  const run = async (url: string, body: unknown = {}) => {
    const reply = await postJson<Reply>(url, body);
    if (reply.status === 200 && reply.body?.saved) return true;
    toast.show(errorOf(L, reply.body));
    return false;
  };

  const unlock = async (person: Person) => {
    if (!(await run(`${base}/${person.userId}/unlock`))) return;
    onChanged();
    // D8: desbloquear aplica al instante con «Deshacer» (vuelve a bloquear con un motivo que lo dice).
    toast.show(formatText(L("ADM_PPL_UNLOCKED"), person.name), {
      label: L("ADM_UNDO"),
      run: () =>
        void run(`${base}/${person.userId}/block`, { reason: L("ADM_PPL_UNDO_UNLOCK_REASON") }).then(
          (done) => done && onChanged(),
        ),
    });
  };

  const items = (person: Person, options: { withBlock: boolean; withUnlock: boolean }): MenuItem[] => {
    const list: MenuItem[] = [];
    if (options.withUnlock && person.status === "blocked")
      list.push({ label: L("ADM_PPL_UNLOCK"), onSelect: () => void unlock(person) });
    list.push(
      { label: L("ADM_PPL_RESET_MENU"), onSelect: () => setDialog({ kind: "reset", person }) },
      { label: L("ADM_PPL_REVOKE_MENU"), onSelect: () => setDialog({ kind: "revoke", person }) },
    );
    if (options.withBlock && person.status !== "blocked")
      list.push({ label: L("ADM_PPL_BLOCK_MENU"), onSelect: () => setDialog({ kind: "block", person }) });
    if (person.status !== "inactive")
      list.push(
        person.isOwner
          ? // E69: la acción se ve, desactivada, con la razón.
            {
              label: L("ADM_PPL_DEACTIVATE_MENU"),
              disabled: true,
              line: L("ADM_PPL_OWNER_FIRST"),
              onSelect: () => undefined,
            }
          : {
              label: L("ADM_PPL_DEACTIVATE_MENU"),
              danger: true,
              onSelect: () => setDialog({ kind: "deactivate", person }),
            },
      );
    else
      list.push({
        label: L("ADM_PPL_ACTIVATE"),
        onSelect: () =>
          void run(`${base}/${person.userId}/activate`).then((done) => {
            if (!done) return;
            onChanged();
            toast.show(formatText(L("ADM_PPL_ACTIVATED"), person.name));
          }),
      });
    return list;
  };

  const close = () => setDialog(null);
  const windows: ReactNode = (
    <>
      <ResetWindow
        orgId={orgId}
        person={dialog?.kind === "reset" ? dialog.person : null}
        onClose={close}
        onDone={onChanged}
      />
      <ConfirmWindow
        open={dialog?.kind === "revoke"}
        title={formatText(L("ADM_PPL_REVOKE_TITLE"), dialog?.person.name ?? "")}
        line={
          dialog?.person.sessions != null
            ? formatText(
                L(dialog.person.sessions === 1 ? "ADM_PPL_REVOKE_LINE_ONE" : "ADM_PPL_REVOKE_LINE"),
                dialog.person.sessions,
              )
            : L("ADM_PPL_REVOKE_LINE_ALL")
        }
        action={L("ADM_PPL_REVOKE_ACTION")}
        onClose={close}
        onConfirm={async () => {
          const person = dialog!.person;
          if (!(await run(`${base}/${person.userId}/sessions/revoke`))) return false;
          onChanged();
          toast.show(
            person.sessions != null
              ? formatText(
                  L(person.sessions === 1 ? "ADM_PPL_REVOKED_ONE" : "ADM_PPL_REVOKED"),
                  person.sessions,
                  person.name,
                )
              : formatText(L("ADM_PPL_REVOKED_ALL"), person.name),
          );
          return true;
        }}
      />
      <ConfirmWindow
        open={dialog?.kind === "deactivate"}
        title={formatText(L("ADM_PPL_DEACTIVATE_TITLE"), dialog?.person.name ?? "")}
        line={formatText(L("ADM_PPL_DEACTIVATE_LINE"), orgName)}
        action={L("ADM_PPL_DEACTIVATE")}
        onClose={close}
        onConfirm={async () => {
          const person = dialog!.person;
          if (!(await run(`${base}/${person.userId}/deactivate`))) return false;
          onChanged();
          toast.show(formatText(L("ADM_PPL_DEACTIVATED"), person.name), {
            label: L("ADM_UNDO"),
            run: () => void run(`${base}/${person.userId}/activate`).then((done) => done && onChanged()),
          });
          return true;
        }}
      />
      <BlockWindow
        orgId={orgId}
        person={dialog?.kind === "block" ? dialog.person : null}
        onClose={close}
        onDone={onChanged}
      />
      <ImpersonateWindow
        orgId={orgId}
        open={dialog?.kind === "open"}
        person={dialog?.kind === "open" ? dialog.person : null}
        onClose={close}
      />
    </>
  );
  return {
    items,
    windows,
    unlock,
    openReset: (person: Person) => setDialog({ kind: "reset", person }),
    openRevoke: (person: Person) => setDialog({ kind: "revoke", person }),
    openBlock: (person: Person) => setDialog({ kind: "block", person }),
    openImpersonate: (person: Person) => setDialog({ kind: "open", person }),
  };
}

/** C12 · confirmación: Cancelar a la izquierda de la acción de peligro; queda abierta si falla. */
export function ConfirmWindow({
  open,
  title,
  line,
  action,
  danger = true,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  line: ReactNode;
  action: string;
  danger?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<boolean>;
}) {
  const { L } = useTexts();
  const [busy, setBusy] = useState(false);
  return (
    <Window
      open={open}
      confirm
      title={title}
      onClose={() => !busy && onClose()}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className={`tm-btn primary${danger ? ` ${ui.dangerBtn}` : ""}`}
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => {
              setBusy(true);
              void onConfirm().then((done) => {
                setBusy(false);
                if (done) onClose();
              });
            }}
          >
            {action}
          </button>
        </>
      }
    >
      <p className="tm-confirm-line">{line}</p>
    </Window>
  );
}

/** `reset` · Generar una temporal o escribirla; al terminar, la temporal una sola vez (C51), nunca en la URL ni guardada. */
function ResetWindow({
  orgId,
  person,
  onClose,
  onDone,
}: {
  orgId: number;
  person: Person | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L } = useTexts();
  const [mode, setMode] = useState<"auto" | "manual">("auto");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [force, setForce] = useState(true);
  const [revoke, setRevoke] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ secret: string | null; revoked: boolean } | null>(null);
  const [shownFor, setShownFor] = useState<number | null>(null);
  // Cada vez que se abre para una persona, la ventana empieza limpia.
  if ((person?.userId ?? null) !== shownFor) {
    setShownFor(person?.userId ?? null);
    setMode("auto");
    setPassword("");
    setConfirm("");
    setForce(true);
    setRevoke(true);
    setErrors({});
    setGeneral(null);
    setResult(null);
  }
  const finish = () => {
    setResult(null);
    onClose();
  };
  const submit = async () => {
    const found: Record<string, string> = {};
    if (mode === "manual") {
      if (password.length < 12 || password.length > 200) found.password = L("ADD_ERR_WEAK");
      else if (password !== confirm) found.confirm = L("ADD_ERR_CONFIRM");
    }
    setErrors(found);
    setGeneral(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    const reply = await postJson<Reply & { sessionsRevoked?: boolean }>(
      `/api/admin/organizations/${orgId}/members/${person!.userId}/password-reset`,
      { mode, password: mode === "manual" ? password : null, forceChange: force, revokeSessions: revoke },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      setPassword("");
      setConfirm("");
      setResult({ secret: reply.body.temporaryPassword ?? null, revoked: !!reply.body.sessionsRevoked });
      onDone();
      return;
    }
    if (reply.body?.field === "password") setErrors({ password: L(reply.body.messageKey ?? "ADD_ERR_WEAK") });
    else setGeneral(errorOf(L, reply.body));
  };
  const name = person?.name ?? "";
  if (result)
    return (
      <Window
        open={!!person}
        title={L("ADM_PPL_RESET_DONE")}
        onClose={finish}
        footer={
          <button type="button" className="tm-btn primary" onClick={finish}>
            {L("ADM_PPL_DONE")}
          </button>
        }
      >
        {result.secret ? (
          <OnceSecret
            label={L("ADM_SAMPLE_SECRET_LABEL")}
            value={result.secret}
            help={[
              L("ADM_PPL_SECRET_ONCE"),
              force ? L("ADM_PPL_MUST_CHANGE") : null,
              result.revoked ? L("ADM_PPL_SESSIONS_CLOSED") : null,
            ]
              .filter(Boolean)
              .join(" ")}
            copiedMessage={L("ADM_SAMPLE_SECRET_COPIED")}
          />
        ) : (
          <Notice tone="done">{formatText(L("ADM_PPL_RESET_MANUAL_DONE"), name)}</Notice>
        )}
      </Window>
    );
  return (
    <Window
      open={!!person}
      title={formatText(L("ADM_PPL_RESET_TITLE"), name)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className="tm-btn primary"
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void submit()}
          >
            {L("ADM_PPL_RESET_ACTION")}
          </button>
        </>
      }
    >
      <div className={ui.windowBlock}>
        {general ? <Notice tone="error">{general}</Notice> : null}
        <span className={styles.fieldTitle} id="reset-mode">
          {L("ADD_PWD_MODE_LABEL")}
        </span>
        <RadioCards
          label={L("ADD_PWD_MODE_LABEL")}
          value={mode}
          onChange={(value) => setMode(value as "auto" | "manual")}
          options={[
            { value: "auto", title: L("ADM_PPL_RESET_AUTO"), line: L("ADM_PPL_RESET_AUTO_LINE") },
            { value: "manual", title: L("ADM_PPL_RESET_MANUAL"), line: L("ADM_PPL_RESET_MANUAL_LINE") },
          ]}
        />
        {mode === "manual" ? (
          <>
            <PasswordField
              id="reset-password"
              label={L("ADD_MANUAL_PWD")}
              value={password}
              autoComplete="new-password"
              error={errors.password}
              help={L("ADM_PPL_PASSWORD_HELP")}
              onChange={(value) => {
                setPassword(value);
                if (value.length >= 12) setErrors((now) => ({ ...now, password: "" }));
              }}
            />
            <PasswordField
              id="reset-confirm"
              label={L("ADD_MANUAL_PWD_CONFIRM")}
              value={confirm}
              autoComplete="new-password"
              error={errors.confirm}
              onChange={(value) => {
                setConfirm(value);
                if (value === password) setErrors((now) => ({ ...now, confirm: "" }));
              }}
            />
          </>
        ) : null}
        <span className={styles.fieldTitle} id="reset-more">
          {L("ADM_PPL_ALSO")}
        </span>
        <div role="group" aria-labelledby="reset-more" className={ui.checks}>
          <Check checked={force} onChange={setForce}>
            {L("ADM_PPL_FORCE_CHANGE")}
          </Check>
          <Check checked={revoke} onChange={setRevoke}>
            {L("ADM_PPL_CLOSE_SESSIONS")}
          </Check>
        </div>
      </div>
    </Window>
  );
}

/** C35 · casilla (botón con role=checkbox, toda la fila se toca). */
export function Check({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      className={ui.check}
      aria-checked={checked ? "true" : "false"}
      onClick={() => onChange(!checked)}
    >
      <span className={ui.box} aria-hidden="true">
        {checked ? <ChatIcon name="check" /> : null}
      </span>
      <span>{children}</span>
    </button>
  );
}

/** `bloquear` · motivo obligatorio (hasta 100); no podrá entrar hasta que la desbloquees. */
function BlockWindow({
  orgId,
  person,
  onClose,
  onDone,
}: {
  orgId: number;
  person: Person | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [shownFor, setShownFor] = useState<number | null>(null);
  if ((person?.userId ?? null) !== shownFor) {
    setShownFor(person?.userId ?? null);
    setReason("");
    setError(null);
  }
  const submit = async () => {
    if (!reason.trim()) return setError(L("ORGCORE_REASON_REQUIRED"));
    setBusy(true);
    const reply = await postJson<Reply>(`/api/admin/organizations/${orgId}/members/${person!.userId}/block`, {
      reason: reason.trim(),
    });
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved) {
      onClose();
      onDone();
      toast.show(formatText(L("ADM_PPL_BLOCKED"), person!.name));
      return;
    }
    setError(errorOf(L, reply.body));
  };
  return (
    <Window
      open={!!person}
      title={formatText(L("ADM_PPL_BLOCK_TITLE"), person?.name ?? "")}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
            {L("ACTION_CANCEL")}
          </button>
          <button
            type="button"
            className={`tm-btn primary ${ui.dangerBtn}`}
            disabled={busy}
            aria-busy={busy || undefined}
            onClick={() => void submit()}
          >
            {L("ADM_PPL_BLOCK")}
          </button>
        </>
      }
    >
      <p className={styles.windowText}>{L("ADM_PPL_BLOCK_LINE")}</p>
      <TextField
        id="block-reason"
        label={L("ADM_PPL_REASON")}
        value={reason}
        maxLength={100}
        error={error}
        help={L("ADM_PPL_REASON_100")}
        placeholder={L("ADM_PPL_BLOCK_EXAMPLE")}
        onChange={(value) => {
          setReason(value);
          if (value.trim()) setError(null);
        }}
      />
    </Window>
  );
}

type Candidate = { userId: number; name: string; email: string };

/**
 * `abrir` · Abrir como usuario: la persona (fija desde su ficha; desde el Resumen, cualquier persona activa) y el
 * motivo; después, el enlace de un solo uso que abre el Company Portal en otra pestaña.
 */
export function ImpersonateWindow({
  orgId,
  open,
  person,
  onClose,
}: {
  orgId: number;
  open: boolean;
  person: Person | null;
  onClose: () => void;
}) {
  const { L } = useTexts();
  const toast = useToast();
  const [target, setTarget] = useState("");
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [people, setPeople] = useState<Candidate[] | null>(null);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setTarget(person ? String(person.userId) : "");
      setReason("");
      setErrors({});
      setGeneral(null);
      setUrl(null);
      if (!person) {
        setPeople(null);
        void getJson<{ items: Candidate[] }>(
          `/api/admin/organizations/${orgId}/members?status=active&pageSize=100`,
        ).then((reply) => setPeople(reply.status === 200 && reply.body ? reply.body.items : []));
      }
    }
  }
  const options = person
    ? [{ value: String(person.userId), label: person.name }]
    : (people ?? []).map((item) => ({ value: String(item.userId), label: `${item.name} · ${item.email}` }));
  const chosen = options.find((option) => option.value === target)?.label.split(" · ")[0] ?? "";
  const submit = async () => {
    const found: Record<string, string> = {};
    if (!target) found.target = L("ADM_PPL_PERSON_REQUIRED");
    if (!reason.trim()) found.reason = L("SUPPORT_REASON_REQUIRED");
    setErrors(found);
    setGeneral(null);
    if (Object.keys(found).length) return;
    setBusy(true);
    const reply = await postJson<{ saved?: boolean; url?: string; field?: string; messageKey?: string }>(
      `/api/admin/organizations/${orgId}/impersonation`,
      { targetUserId: Number(target), reason: reason.trim() },
    );
    setBusy(false);
    if (reply.status === 200 && reply.body?.saved && reply.body.url) return setUrl(reply.body.url);
    if (reply.body?.field === "reason") setErrors({ reason: L(reply.body.messageKey ?? "SUPPORT_REASON_REQUIRED") });
    else setGeneral(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
  };
  return (
    <Window
      open={open}
      title={L("ADM_PPL_OPEN_AS")}
      onClose={onClose}
      footer={
        url ? (
          <>
            <button type="button" className="tm-btn secondary" onClick={() => (setUrl(null), setReason(""))}>
              {L("ADM_PPL_OPEN_ANOTHER")}
            </button>
            <a
              className="tm-btn primary"
              href={url}
              target="_blank"
              rel="noopener"
              onClick={() => {
                toast.show(formatText(L("ADM_PPL_OPENED_AS"), chosen));
                // El enlace sirve una sola vez: se olvida al usarlo.
                setUrl(null);
                onClose();
              }}
            >
              {L("ADM_PPL_OPEN_TAB")}
            </a>
          </>
        ) : (
          <>
            <button type="button" className="tm-btn secondary" onClick={onClose} disabled={busy}>
              {L("ACTION_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy}
              aria-busy={busy || undefined}
              onClick={() => void submit()}
            >
              {L("ADM_PPL_PREPARE")}
            </button>
          </>
        )
      }
    >
      {url ? (
        <Notice tone="done">{formatText(L("ADM_PPL_ACCESS_READY"), chosen)}</Notice>
      ) : (
        <div className={ui.windowBlock}>
          <p className={styles.windowText}>{L("ADM_PPL_OPEN_LINE")}</p>
          {general ? <Notice tone="error">{general}</Notice> : null}
          <div className={`tm-field${errors.target ? " invalid" : ""}`}>
            <label htmlFor="open-person">{L("ADM_PPL_PERSON")}</label>
            <ChoiceMenu
              id="open-person"
              label={L("ADM_PPL_PERSON")}
              value={target}
              disabled={!!person || people === null}
              placeholder={people === null && !person ? L("NPA_LOADING") : L("ADM_PPL_CHOOSE_PERSON")}
              options={options}
              onChange={(value) => {
                setTarget(value);
                setErrors((now) => ({ ...now, target: "" }));
              }}
              invalid={!!errors.target}
              searchLabel={L("CFG1_SEARCH")}
              clearSearchLabel={L("CFG1_R1_CLEAR_SEARCH")}
              searchIcon={<ChatIcon name="search" />}
              clearIcon={<ChatIcon name="x" />}
              chevron={<ChatIcon name="chevron-down" />}
              check={<ChatIcon name="check" />}
            />
            {errors.target ? <span className="tm-field-error">{errors.target}</span> : null}
          </div>
          <div className={`tm-field${errors.reason ? " invalid" : ""}`}>
            <label htmlFor="open-reason">{L("ADM_PPL_REASON")}</label>
            <textarea
              id="open-reason"
              className={styles.textarea}
              value={reason}
              maxLength={500}
              placeholder={L("ADM_PPL_OPEN_EXAMPLE")}
              aria-invalid={errors.reason ? "true" : undefined}
              aria-describedby="open-reason-help"
              onChange={(event) => {
                setReason(event.target.value);
                if (event.target.value.trim()) setErrors((now) => ({ ...now, reason: "" }));
              }}
            />
            {errors.reason ? <span className="tm-field-error">{errors.reason}</span> : null}
            <small id="open-reason-help" className="tm-field-help">
              {L("ADM_PPL_REASON_500")}
            </small>
          </div>
        </div>
      )}
    </Window>
  );
}
